/**
 * Der "aktuelle Stand" im Projekt-Archiv (Buchhaltung, Knopf "Archiv / PDFs"),
 * ohne React — damit die Regeln ohne jsdom pruefbar sind.
 *
 * Ist der laufende Bericht noch nicht archiviert, steht er als eigener Eintrag
 * neben den Archivzeilen, damit sein PDF schon vor dem Versand ansehbar ist.
 * Er ist KEINE Zeile in `report_history`: er hat keine echte Kennung und
 * laesst sich nicht loeschen.
 */
import type { Report, ReportHistoryEntry, ReportMetadata } from '../types';

/** Kennung des Live-Eintrags. Dieselbe lehnt `deleteReportHistory` ab. */
export const LIVE_ARCHIV_EINTRAG_ID = 'live-current';

/** Was vom Archiv fuer den Abgleich gebraucht wird. */
type ArchivKopf = Pick<ReportHistoryEntry, 'projectNumber' | 'inspectionNumber'>;

/**
 * Braucht das Archiv einen Live-Eintrag? Nur fuer einen Bericht mit Nummer,
 * der noch nicht als Archivzeile dasteht (gleiche Projekt- und Berichtsnummer).
 *
 * Die Buchhaltung fragt das mit den Metadaten des LISTENEINTRAGS — so wird das
 * volle Projekt nur geladen, wenn der Eintrag wirklich gebraucht wird.
 */
export const brauchtLiveArchivEintrag = (
    archiv: ArchivKopf[],
    meta: Pick<ReportMetadata, 'projectNumber' | 'inspectionNumber'> | null | undefined,
): boolean => {
    if (!meta || !(meta.inspectionNumber > 0)) return false;
    return !archiv.some(h => h.projectNumber === meta.projectNumber && h.inspectionNumber === meta.inspectionNumber);
};

/**
 * Der Live-Eintrag aus dem VOLL geladenen Projekt (`loadProjectById`), oder
 * null, wenn keiner gebraucht wird.
 *
 * Nie aus einem Listeneintrag bauen: dessen `data` traegt nur `metadata`, ohne
 * `items`, `settings` und `checklists`. Das PDF des aktuellen Stands kam so
 * ohne Maengel und ohne Firmendaten heraus, und Erzeuger, die `items`
 * voraussetzen, brachen ab.
 *
 * Die Pruefung laeuft hier noch einmal gegen die vollen Metadaten: war die
 * Liste veraltet und ist der Bericht inzwischen archiviert, entsteht kein
 * zweiter Eintrag mit derselben Nummer.
 */
export const liveArchivEintrag = (
    archiv: ArchivKopf[],
    bericht: Report | null | undefined,
): ReportHistoryEntry | null => {
    const meta = bericht?.metadata;
    if (!brauchtLiveArchivEintrag(archiv, meta)) return null;
    return {
        id: LIVE_ARCHIV_EINTRAG_ID,
        projectNumber: meta.projectNumber,
        projectName: meta.projectName,
        inspectionNumber: meta.inspectionNumber,
        sectionId: meta.sectionId,
        date: meta.date,
        time: meta.time || '',
        conductedByName: meta.conductedBy || meta.coordinator,
        reportData: bericht,
    };
};

/**
 * Bietet die Archivliste fuer diesen Eintrag "Loeschen" an? Fuer den
 * Live-Eintrag nicht: er ist keine Archivzeile, `deleteReportHistory` lehnt ihn
 * ab, und der Knopf fuehrte erst zur Rueckfrage "unwiderruflich loeschen?" und
 * danach zu "Fehler beim Loeschen".
 */
export const istArchivEintragLoeschbar = (eintrag: Pick<ReportHistoryEntry, 'id'>): boolean =>
    !!eintrag.id && eintrag.id !== LIVE_ARCHIV_EINTRAG_ID;
